import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';

import {withI18n} from 'spa/Components/Context';

import FormGroup from './FormGroup';
import Textarea from './Textarea';

import {cn} from 'spa/css';

import './InputField.less';

import errorIcon from 'spa/images/icons/error-new.svg';

@withI18n
class InputField extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    className: PropTypes.string,
    groupClassName: PropTypes.string,
    placeholder: PropTypes.string,
    type: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.oneOf([
        'text',
        'url',
        'tel',
        'email',
        'password',
        'textarea',
        'number',
        'hidden',
      ]),
    ]),
    children: PropTypes.any,
    error: PropTypes.string,
    icon: PropTypes.oneOfType([
      PropTypes.string, // a url
      PropTypes.node,
    ]),
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    pattern: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    required: PropTypes.bool,
    wrap: PropTypes.func,
    validateOnChange: PropTypes.bool,
    skipInitialCheck: PropTypes.bool,
    newLayout: PropTypes.bool,
    darkLayout: PropTypes.bool,
  };

  static defaultProps = {
    type: 'text',
    wrap: x => x,
  };

  static contextTypes = {
    formHasSubmitted: PropTypes.bool,
  };

  state = {touched: false, errors: [], isValid: true};

  componentDidMount() {
    const {skipInitialCheck} = this.props;

    if (!skipInitialCheck) {
      this.checkValidity();
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.error !== this.props.error) {
      const {input} = this;
      if (input && input.setCustomValidity) {
        input.setCustomValidity(nextProps.error || '');
        this.checkValidity();
      }
    }
  }
  componentDidUpdate(prevProps) {
    if (
      prevProps.type !== this.props.type ||
      prevProps.required !== this.props.required ||
      prevProps.pattern !== this.props.pattern ||
      prevProps.maxLength !== this.props.maxLength ||
      prevProps.minLength !== this.props.minLength
    ) {
      this.checkValidity();
    }
  }

  get validity() {
    return this.input && this.input.validity;
  }

  @autobind inputRef(input) {
    // unwrap
    while (input && input.wrappedInstance) {
      input = input.wrappedInstance;
    }

    this.input = input;

    if (input && input.setCustomValidity) input.setCustomValidity(this.props.error || '');
  }

  @autobind onChange(event) {
    if (this.props.onChange) this.props.onChange(event);
    if (this.props.validateOnChange) {
      this.setState({touched: true});
      this.checkValidity();
    }
  }

  @autobind onBlur(event) {
    this.input.setCustomValidity('');

    if (this.props.onBlur) this.props.onBlur(event);

    this.setState({touched: true});
    this.checkValidity();
  }

  checkValidity() {
    const errors = [];
    const {input} = this;

    if (!input) {
      this.setState({errors});
      return;
    }

    const {i18n} = this.context;
    const {validity} = input;
    let isValid = true;
    if (validity) {
      isValid = validity.valid;
      if (validity.valueMissing) {
        errors.push({
          key: 'valueMissing',
          message: i18n.__('%(label)s is required', this.props),
        });
      }
      if (validity.tooShort) {
        errors.push({
          key: 'tooShort',
          message: i18n.__('%(label)s is too short', this.props),
        });
      }
      if (validity.tooLong) {
        errors.push({
          key: 'tooLong',
          message: i18n.__('%(label)s is too long', this.props),
        });
      }
      if (validity.patternMismatch) {
        errors.push({
          key: 'patternMismatch',
          message: i18n.__('%(label)s is invalid', this.props),
        });
      }
      if (validity.typeMismatch && !validity.patternMismatch) {
        errors.push({
          key: 'typeMismatch',
          message: i18n.__('%(label)s must be valid', this.props),
        });
      }
    }

    this.setState({errors, isValid});
  }

  render() {
    let {
      id,
      label,
      children,
      className,
      groupClassName,
      placeholder,
      type,
      error,
      icon,
      name,
      disabled,
      readOnly,
      wrap,
      validateOnChange,
      skipInitialCheck,
      newLayout,
      darkLayout,
      ...rest
    } = this.props;
    const {isValid, errors, touched} = this.state;
    const {formHasSubmitted, i18n} = this.context;

    const isInvalid = (touched || formHasSubmitted) && (!isValid || error);

    let Tag = 'input';
    if (typeof type === 'function') {
      Tag = type;
      type = undefined;
    } else if (type === 'textarea') {
      type = undefined;
      Tag = Textarea;
    }

    const input = wrap([
      <Tag
        key="input"
        id={id}
        className={cn('form-control', className, isInvalid && 'form-control--invalid')}
        data-qa={`${name}-input`}
        name={name}
        {...rest}
        onChange={this.onChange}
        onBlur={this.onBlur}
        type={type}
        disabled={disabled}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={this.inputRef}
      />,
      <div
        key="line"
        className="form-field-line"
      />,
    ]);

    return (
      <div>
        <FormGroup
          label={label}
          htmlFor={id}
          className={cn(
            'form-group--input',
            isInvalid && 'form-group--invalid',
            disabled && 'form-group--disabled',
            readOnly && 'form-group--read-only',
            newLayout && 'form-group--new-layout',
            newLayout && darkLayout && 'form-group--dark-layout',
            groupClassName,
          )}>
          {newLayout && isInvalid &&
            <img className="form-field__error-icon" src={errorIcon} alt={i18n._x('Signup error icon', 'Form error icon')} />
          }
          <div className="form-field">
            {icon !== undefined ? (
              <div className="centered-row centered-row--flexible" style={{width: '100%'}}>
                <div className="col-11">
                  {input}
                </div>
                {icon && (
                  <div className="col-1 input-field__icon">
                    {typeof icon === 'string' ? <img src={icon} alt="" /> : icon}
                  </div>
                )}
              </div>
            ) : input}
          </div>
          {children}
          {!newLayout && isInvalid && (
            [{key: 'custom', message: error}, ...errors]
              .map(({key, message}) =>
                (message ? <p data-qa={`${name}-input-error`} key={key} className="form-field-error">{message}</p> : null))
          )}
        </FormGroup>
        {newLayout && isInvalid && (
          [{key: 'custom', message: error}, ...errors]
            .map(({key, message}) =>
              (message ? <p key={key} className="form-field-error form-field-error--new-layout" data-qa={`${name}-input-error`}>{message}</p> : null))
        )}
      </div>
    );
  }
}

export default InputField;



// WEBPACK FOOTER //
// ./spa/Components/Form/InputField.jsx